import React, { useCallback } from 'react';
import { useMessageSource } from 'react-message-source';
import Loader from '@uiParts/loader';
import { useFeatureFlagContext } from '../providers/FeatureFlagProvider';
import FeatureFlagList from './FeatureFlagList';
import './style.scss';

const FeatureFlagSettingsSection: React.FC = () => {
  const { getMessage } = useMessageSource();
  const {
    flags,
    unlock,
    isLoading,
    reloadRequired,
    savingFlags,
    isUnlockSaving,
    updateStatus,
    updateFlag,
    updateUnlock,
    clearStatus,
  } = useFeatureFlagContext();

  const handleToggle = useCallback(
    (flagKey: string, enabled: boolean) => {
      updateFlag(flagKey, enabled);
    },
    [updateFlag],
  );

  const handleUnlockToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateUnlock(event.target.checked);
    },
    [updateUnlock],
  );

  return (
    <div className="card feature-flags-section">
      <p className="subtitle">
        <b>{getMessage('featureFlag.title')}</b>
      </p>
      {reloadRequired && (
        <div
          role="status"
          aria-live="polite"
          style={{
            padding: '8px 12px',
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: 4,
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span>設定を反映するにはページをリロードしてください。</span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{ padding: '2px 10px', cursor: 'pointer' }}
          >
            リロード
          </button>
        </div>
      )}
      {updateStatus && (
        <div
          className={`feature-flags-section__status feature-flags-section__status--${updateStatus.type}`}
          role="alert"
          aria-live="polite"
        >
          <span>{updateStatus.message}</span>
          <button
            type="button"
            className="feature-flags-section__status-close"
            onClick={clearStatus}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>
      )}
      {isLoading ? (
        <aside className="load-area">
          <Loader />
        </aside>
      ) : (
        <>
          <div className="feature-flags-section__unlock">
            <div className="feature-flag-toggle">
              <div className="feature-flag-toggle__label">
                <span className="feature-flag-toggle__title">
                  {getMessage('featureFlag.unlock')}
                </span>
              </div>
              <label
                className={`feature-flag-toggle__switch ${isUnlockSaving ? 'feature-flag-toggle__switch--saving' : ''}`}
                aria-label={getMessage('featureFlag.unlock')}
              >
                <input
                  type="checkbox"
                  checked={unlock}
                  disabled={isUnlockSaving}
                  onChange={handleUnlockToggle}
                  aria-describedby="unlock-description"
                />
                <span className="feature-flag-toggle__slider" />
                {isUnlockSaving && (
                  <span className="feature-flag-toggle__saving-indicator" aria-hidden="true" />
                )}
              </label>
            </div>
            <span id="unlock-description" className="sr-only">
              全ての機能フラグを一括で有効化します
            </span>
          </div>
          <FeatureFlagList
            flags={flags}
            disabled={unlock}
            savingFlags={savingFlags}
            onToggle={handleToggle}
          />
        </>
      )}
    </div>
  );
};

export default FeatureFlagSettingsSection;
